import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { getDraftById, swapDraftSlots, startDraft, joinDraft, updateTeamName } from '../services/api';

// Own lightweight poll loop rather than DraftContext's pollDraftRoom -- this view is deliberately
// standalone (not DraftContext-backed, see DraftCommissionerSetupView's comment), so it can't share
// that provider's poll chain. Same interval/visibility-pause behavior, simpler machinery since
// there's only ever one active loop tied to this component's own lifetime (no token-invalidation
// chain needed -- the interval is torn down directly on unmount/draftId change).
export const LOBBY_POLL_INTERVAL_MS = 4000;

/**
 * Commissioner Mode pre-start waiting room: shows every slot's current occupant, lets the
 * commissioner reorder slots, and displays the shareable join code/link/QR for inviting more
 * guests. Deliberately standalone (not DraftContext-backed -- see DraftCommissionerSetupView's
 * comment) so it works the same for a logged-in commissioner and an anonymous guest who just
 * joined. Polls for live updates and auto-navigates every viewer to the board once the
 * commissioner starts the draft.
 *
 * No AI-opponent assignment here (unlike classic Draft Mode's opponent linking): an AI-linked
 * slot has no human drafter, and Commissioner Mode's per-slot pick authorization has no auto-pick
 * path for one -- its turn would just stall the live draft with no one able to pick for it.
 */
function DraftLobbyView() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [joiningAsDrafter, setJoiningAsDrafter] = useState(false);
  const [commissionerTeamName, setCommissionerTeamName] = useState('');

  const refetch = useCallback(() => {
    return getDraftById(draftId)
      .then(fresh => {
        setDraft(fresh);
        return fresh;
      })
      .catch(err => setError(err.response?.data?.error?.message || 'Failed to load draft'));
  }, [draftId]);

  useEffect(() => {
    setLoading(true);
    refetch().finally(() => setLoading(false));
  }, [refetch]);

  // Live sync: poll while this draft is still in the lobby, and auto-navigate every viewer to the
  // board the moment the commissioner starts it (not just the commissioner's own browser).
  useEffect(() => {
    let cancelled = false;
    const id = setInterval(() => {
      if (cancelled || document.hidden) return;
      refetch().then(fresh => {
        if (!cancelled && fresh?.status === 'active') {
          navigate(`/draft/${draftId}`);
        }
      });
    }, LOBBY_POLL_INTERVAL_MS);

    function handleVisibilityChange() {
      if (!document.hidden && !cancelled) {
        refetch().then(fresh => {
          if (!cancelled && fresh?.status === 'active') {
            navigate(`/draft/${draftId}`);
          }
        });
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [draftId, refetch, navigate]);

  const isCommissioner = !!user && draft?.ownerId === user.userId;

  async function handleMove(slotIndex, direction) {
    const target = slotIndex + direction;
    if (!draft || target < 0 || target >= draft.teams.length) return;
    try {
      await swapDraftSlots(draftId, slotIndex, target);
      await refetch();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to reorder slots');
    }
  }

  async function handleJoinAsDrafter() {
    if (!draft?.joinCode) return;
    setJoiningAsDrafter(true);
    setError(null);
    try {
      const result = await joinDraft(draft.joinCode);
      if (commissionerTeamName.trim()) {
        await updateTeamName(draftId, result.slotIndex, commissionerTeamName.trim());
      }
      await refetch();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to join as a drafter');
    } finally {
      setJoiningAsDrafter(false);
    }
  }

  async function handleStart() {
    setStarting(true);
    setError(null);
    try {
      await startDraft(draftId);
      navigate(`/draft/${draftId}`);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to start the draft');
      setStarting(false);
    }
  }

  if (loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading draft room...</p>
      </div>
    );
  }

  if (!draft) {
    return <div className='error-banner'>{error || 'Draft not found.'}</div>;
  }

  const occupantLabel = (team) => {
    if (team.opponent) return `🤖 ${team.opponent.displayName}`;
    if (team.memberUserId) return `${team.teamName} (joined)`;
    if (team.guestDisplayName) return `${team.guestDisplayName} (guest)`;
    return 'Waiting for a drafter...';
  };
  const isOccupied = (team) => !!(team.opponent || team.memberUserId || team.guestDisplayName);
  const allOccupied = draft.teams.every(isOccupied);
  // The commissioner's own occupied slot, if any -- distinct from isOwnerSlot, which Commissioner
  // Mode drafts never set (see DraftService.createDraft). Computed for every viewer, not just the
  // commissioner themselves, so anyone in the lobby can see who's running the room.
  const commissionerSlot = draft.teams.find(t => t.memberUserId === draft.ownerId);
  const commissionerHasJoined = isCommissioner && !!commissionerSlot;

  const joinLink = `${window.location.origin}/draft/join/${draft.joinCode || ''}`;

  return (
    <div className='card' style={{ maxWidth: 640, margin: '0 auto' }}>
      <h2>{draft.draftName || 'Draft Room'}</h2>
      <p style={{ color: 'var(--text-muted)' }}>Waiting for everyone to join...</p>

      {draft.joinCode && (
        <div className='draft-lobby-invite' style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '16px 0' }}>
          {/* Explicit white card, independent of page theme -- a small bright QR floating directly
              on Cool mode's pure-black background is a known camera-exposure scan-failure mode. */}
          <div style={{ background: '#fff', padding: 12, borderRadius: 8, lineHeight: 0 }}>
            <QRCodeSVG value={joinLink} size={160} marginSize={4} level='M' />
          </div>
          <div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '0.1em' }}>{draft.joinCode}</div>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={() => navigator.clipboard?.writeText(joinLink)}
            >
              Copy Invite Link
            </button>
            <button
              type='button'
              className='btn btn-secondary'
              style={{ marginLeft: 8 }}
              onClick={() => navigate(`/draft/${draftId}/tv`)}
            >
              📺 Open TV Display
            </button>
          </div>
        </div>
      )}

      {error && <div className='error-banner'>{error}</div>}

      <ul style={{ listStyle: 'none', padding: 0 }}>
        {draft.teams.map((team, i) => (
          <li key={team.slotIndex} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ width: 24, color: 'var(--text-muted)' }}>{i + 1}</span>
            <span style={{ flex: 1 }}>{occupantLabel(team)}</span>
            {team === commissionerSlot && (
              <span className='badge' style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                Commissioner
              </span>
            )}
            {isCommissioner && (
              <>
                <button type='button' aria-label={`Move slot ${i + 1} up`} onClick={() => handleMove(i, -1)} disabled={i === 0}>↑</button>
                <button type='button' aria-label={`Move slot ${i + 1} down`} onClick={() => handleMove(i, 1)} disabled={i === draft.teams.length - 1}>↓</button>
              </>
            )}
          </li>
        ))}
      </ul>

      {!commissionerSlot && (
        <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Commissioner — not drafting</p>
      )}

      {isCommissioner && !commissionerHasJoined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            className='input'
            aria-label='Your team name'
            placeholder='Your team name (optional)'
            value={commissionerTeamName}
            onChange={e => setCommissionerTeamName(e.target.value)}
            maxLength={200}
            disabled={joiningAsDrafter}
          />
          <button
            type='button'
            className='btn btn-secondary'
            onClick={handleJoinAsDrafter}
            disabled={joiningAsDrafter}
          >
            {joiningAsDrafter ? 'Joining…' : 'Join as a Drafter'}
          </button>
        </div>
      )}

      {isCommissioner && (
        <button
          type='button'
          className='btn btn-primary'
          onClick={handleStart}
          disabled={!allOccupied || starting}
          title={!allOccupied ? 'Every slot needs a drafter before you can start' : undefined}
        >
          {starting ? 'Starting…' : 'Start Draft'}
        </button>
      )}
    </div>
  );
}

export default DraftLobbyView;
